import { mapErrorToUserMessage } from '@repo/core';
/**
 * ─── POS Register — Main Sales Terminal ──────────────────────
 * The primary POS interface: browse products, build cart,
 * apply discounts, process payment, print receipt.
 *
 * Features:
 *  - Product catalog grid with category filter & barcode search
 *  - Cart with quantity, discount, line totals
 *  - Payment panel: cash, GCash, Maya, card, split
 *  - Senior/PWD discount toggle
 *  - Sale completion with invoice generation
 *  - Shift awareness (requires open shift)
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import {
  posProductRepo, posSaleRepo, posSaleItemRepo,
  posCategoryRepo, posShiftRepo,
} from '../../../lib/db'
import type { POSProduct, POSCategory, POSSale, POSSaleItem, POSShift, PaymentMethod } from '@repo/entity-pos'
import {
  PRODUCT_UNIT_LABELS, PRODUCT_STATUS_COLORS,
  PAYMENT_METHOD_LABELS, PAYMENT_METHOD_COLORS,
  SALE_STATUS_LABELS,
} from '@repo/entity-pos'
import { POSSaleService } from '@repo/entity-pos'
import {
  Search, ShoppingCart, Plus, Minus, Trash2, Receipt,
  Banknote, CreditCard, Smartphone, Wallet, Printer,
  X, ArrowRight, Package, AlertTriangle, Clock,
  Percent, Ban, ChevronDown, RefreshCw,
} from 'lucide-react'

// ─── Types ──────────────────────────────────────────────────

interface CartItem {
  productId: string
  sku: string
  barcode?: string
  name: string
  unit: string
  quantity: number
  unitPrice: number
  discountPercent: number
  discountAmount: number
  lineTotal: number
  vatAmount: number
  vatRate: number
  isVatInclusive: boolean
  costPrice: number
}

interface PaymentSplit {
  method: PaymentMethod
  amount: number
  reference?: string
}

// ─── Constants ──────────────────────────────────────────────

const QUICK_AMOUNTS = [50, 100, 200, 500, 1000, 2000]

// ─── Main Component ─────────────────────────────────────────

export function POSRegisterPage() {
  const navigate = useNavigate()
  const barcodeRef = useRef<HTMLInputElement>(null)

  // Data
  const [products, setProducts] = useState<POSProduct[]>([])
  const [categories, setCategories] = useState<POSCategory[]>([])
  const [loading, setLoading] = useState(true)

  // Cart
  const [cart, setCart] = useState<CartItem[]>([])

  // UI state
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState('')
  const [showPayment, setShowPayment] = useState(false)
  const [showVoidConfirm, setShowVoidConfirm] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>('cash')
  const [amountTendered, setAmountTendered] = useState('')
  const [saleDiscountPercent, setSaleDiscountPercent] = useState(0)
  const [saleDiscountLabel, setSaleDiscountLabel] = useState('')
  const [splitPayments, setSplitPayments] = useState<PaymentSplit[]>([])
  const [splitAmount, setSplitAmount] = useState('')

  // Shift
  const [activeShift, setActiveShift] = useState<POSShift | null>(null)
  const [showShiftWarning, setShowShiftWarning] = useState(false)

  // Completed sale
  const [completedSale, setCompletedSale] = useState<POSSale | null>(null)

  // ─── Load Data ───────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [prodResult, catResult, shiftResult] = await Promise.all([
        posProductRepo.findMany({ page: 1, pageSize: 1000, sort: [{ field: 'sortOrder', direction: 'asc' }] }),
        posCategoryRepo.findMany({ page: 1, pageSize: 100, sort: [{ field: 'sortOrder', direction: 'asc' }] }),
        posShiftRepo.findMany({ page: 1, pageSize: 10, filter: [{ field: 'status', operator: 'eq', value: 'open' }], sort: [{ field: 'openTime', direction: 'desc' }] }),
      ])

      if ('items' in prodResult) setProducts(prodResult.items as POSProduct[])
      if ('items' in catResult) setCategories(catResult.items as POSCategory[])

      if ('items' in shiftResult && shiftResult.items.length > 0) {
        setActiveShift(shiftResult.items[0] as POSShift)
      } else {
        setShowShiftWarning(true)
      }
    } catch (err) { console.error(err); alert(mapErrorToUserMessage(err).userMessage) } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  // Focus barcode on mount
  useEffect(() => {
    barcodeRef.current?.focus()
  }, [])

  // ─── Filter Products ─────────────────────────────────────

  const filteredProducts = products.filter((p) => {
    if (p.status !== 'active' || !p.isSellable) return false
    if (activeCategory && p.categoryId !== activeCategory) return false
    if (search) {
      const q = search.toLowerCase()
      return (
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        (p.barcode && p.barcode.includes(q))
      )
    }
    return true
  })

  // ─── Cart Operations ─────────────────────────────────────

  const addToCart = (product: POSProduct) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.productId === product.id)
      if (existing) {
        return prev.map((i) =>
          i.productId === product.id
            ? updateCartItem(i, i.quantity + 1, i.discountPercent)
            : i,
        )
      }
      return [...prev, createCartItem(product, 1, 0)]
    })
    // Clear barcode search after add
    setSearch('')
    barcodeRef.current?.focus()
  }

  const handleBarcode = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    const code = search.trim()
    if (!code) return

    const product = products.find(
      (p) => p.barcode === code || p.sku === code,
    )
    if (product) {
      addToCart(product)
    }
  }

  const updateQty = (productId: string, delta: number) => {
    setCart((prev) =>
      prev.map((i) => {
        if (i.productId !== productId) return i
        const newQty = Math.max(0.001, i.quantity + delta)
        if (newQty <= 0) return i
        return updateCartItem(i, newQty, i.discountPercent)
      }).filter((i) => i.quantity > 0),
    )
  }

  const updateDiscount = (productId: string, percent: number) => {
    setCart((prev) =>
      prev.map((i) =>
        i.productId === productId
          ? updateCartItem(i, i.quantity, percent)
          : i,
      ),
    )
  }

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((i) => i.productId !== productId))
  }

  const clearCart = () => {
    setCart([])
    setSaleDiscountPercent(0)
    setSaleDiscountLabel('')
    setAmountTendered('')
    setSplitPayments([])
    setShowPayment(false)
  }

  // ─── Cart Computed ───────────────────────────────────────

  const totals = POSSaleService.computeSaleTotals(
    cart.map((i) => ({
      ...i,
      id: '',
      tenantId: '',
      saleId: '',
      notes: undefined,
      createdAt: 0,
      updatedAt: 0,
      deletedAt: null,
      version: 0,
      createdBy: '',
      updatedBy: '',
    })) as unknown as POSSaleItem[],
    saleDiscountPercent,
  )

  // ─── Payment ─────────────────────────────────────────────

  const openPayment = () => {
    if (cart.length === 0) return
    setAmountTendered(totals.grandTotal.toFixed(2))
    setSelectedPaymentMethod('cash')
    setShowPayment(true)
  }

  const handleSplitAdd = () => {
    const amount = parseFloat(splitAmount)
    if (isNaN(amount) || amount <= 0) return
    setSplitPayments([...splitPayments, { method: selectedPaymentMethod, amount }])
    setSplitAmount('')
  }

  const processSale = async () => {
    if (!activeShift) {
      alert('No open shift. Please open a shift first.')
      return
    }

    const tendered = selectedPaymentMethod === 'split'
      ? splitPayments.reduce((s, p) => s + p.amount, 0)
      : parseFloat(amountTendered)

    if (isNaN(tendered) || tendered <= 0) {
      alert('Please enter amount tendered.')
      return
    }

    const validation = POSSaleService.validatePayment(tendered, totals.grandTotal)
    if (!validation.valid) {
      alert(validation.message)
      return
    }

    try {
      const now = new Date()
      const sale: POSSale = {
        id: crypto.randomUUID(),
        tenantId: 'default',
        branchId: activeShift.branchId,
        shiftId: activeShift.id,
        invoiceNumber: POSSaleService.generateInvoiceNumber(Math.floor(Math.random() * 9999)),
        saleDate: now.toISOString().slice(0, 10),
        saleTime: now.toTimeString().slice(0, 8),
        customerName: 'Walk-in',
        items: cart.map((item) => ({
          id: crypto.randomUUID(),
          tenantId: 'default',
          saleId: '',
          productId: item.productId,
          sku: item.sku,
          barcode: item.barcode,
          name: item.name,
          unit: item.unit as any,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discountPercent: item.discountPercent,
          discountAmount: item.discountAmount,
          lineTotal: item.lineTotal,
          vatAmount: item.vatAmount,
          vatRate: item.vatRate,
          isVatInclusive: item.isVatInclusive,
          costPrice: item.costPrice,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          deletedAt: null,
          version: 1,
          createdBy: 'cashier',
          updatedBy: 'cashier',
        })),
        itemCount: totals.itemCount,
        subtotal: totals.subtotal,
        discountTotal: totals.discountTotal,
        discountPercent: saleDiscountPercent,
        discountLabel: saleDiscountLabel || undefined,
        vatTotal: totals.vatTotal,
        vatExemptAmount: 0,
        grandTotal: totals.grandTotal,
        amountTendered: tendered,
        changeDue: POSSaleService.computeChange(tendered, totals.grandTotal),
        paymentMethod: selectedPaymentMethod,
        paymentBreakdown: selectedPaymentMethod === 'split' ? splitPayments : undefined,
        status: 'completed',
        cashierId: 'cashier',
        cashierName: 'Cashier',
        isOffline: false,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        deletedAt: null,
        version: 1,
        createdBy: 'cashier',
        updatedBy: 'cashier',
      }

      // Update sale items with saleId
      sale.items = sale.items.map((i) => ({ ...i, saleId: sale.id }))

      await posSaleRepo.create(sale)

      // Update shift stats
      if (activeShift) {
        await posShiftRepo.update(activeShift.id, {
          totalSales: activeShift.totalSales + sale.grandTotal,
          transactionCount: activeShift.transactionCount + 1,
          paymentBreakdown: {
            ...activeShift.paymentBreakdown,
            [sale.paymentMethod]: (activeShift.paymentBreakdown?.[sale.paymentMethod] || 0) + sale.grandTotal,
          },
          version: activeShift.version,
        } as any)
      }

      setCompletedSale(sale)
      setShowPayment(false)
    } catch (err) {
      console.error('Sale error:', err)
      alert('Failed to process sale. Please try again.')
    }
  }

  // ─── Void Sale ────────────────────────────────────────────

  const voidSale = async () => {
    if (!completedSale) return
    try {
      await posSaleRepo.update(completedSale.id, {
        status: 'voided',
        voidReason: 'Voided by cashier',
        voidedAt: Date.now(),
        voidedBy: 'cashier',
        version: completedSale.version,
      } as any)
      setCompletedSale(null)
      clearCart()
    } catch (err) { console.error(err); alert(mapErrorToUserMessage(err).userMessage) }
  }

  // ─── Open Shift Quickly ──────────────────────────────────

  const openShift = async () => {
    try {
      const now = new Date()
      const shift: POSShift = {
        id: crypto.randomUUID(),
        tenantId: 'default',
        shiftNumber: `SFT-${now.toISOString().slice(0, 10).replace(/-/g, '')}-01`,
        shiftDate: now.toISOString().slice(0, 10),
        openTime: now.toTimeString().slice(0, 8),
        openedBy: 'cashier',
        openedByName: 'Cashier',
        openingCash: 0,
        expectedCash: 0,
        totalSales: 0,
        transactionCount: 0,
        payouts: [],
        totalPayouts: 0,
        voidCount: 0,
        refundCount: 0,
        status: 'open',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        deletedAt: null,
        version: 1,
        createdBy: 'cashier',
        updatedBy: 'cashier',
      }
      await posShiftRepo.create(shift)
      setActiveShift(shift)
      setShowShiftWarning(false)
    } catch (err) { console.error(err); alert(mapErrorToUserMessage(err).userMessage) }
  }

  // ─── Render ──────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-500">Loading POS Terminal...</p>
      </div>
    )
  }

  if (showShiftWarning) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Card>
          <CardHeader title="No Open Shift" description="You must open a shift before processing sales." />
          <div className="p-6 text-center space-y-4">
            <Clock className="mx-auto h-16 w-16 text-yellow-500" />
            <p className="text-gray-600">Please open a cash register shift to begin accepting sales.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={openShift} icon={<Plus className="h-4 w-4" />}>
                Open Shift
              </Button>
              <Button variant="outline" onClick={() => navigate({ to: '/pos/shifts' })}>
                View Shifts
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Top Bar */}
      <div className="flex items-center justify-between border-b bg-white px-4 py-2 shadow-sm">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold text-gray-900">POS Terminal</h1>
          {activeShift && (
            <Badge color="green" size="sm">
              Shift: {activeShift.shiftNumber}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadData} icon={<RefreshCw className="h-3 w-3" />}>
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/pos/sales' })}>
            Sales History
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/pos/shifts' })}>
            Shifts
          </Button>
        </div>
      </div>

      {/* Main Content: Products + Cart */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Product Catalog */}
        <div className="flex flex-1 flex-col overflow-hidden border-r">
          {/* Search + Barcode */}
          <div className="border-b bg-gray-50 p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                ref={barcodeRef}
                type="text"
                placeholder="Scan barcode or search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleBarcode}
                className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto border-b bg-white px-3 py-2">
            <button
              onClick={() => setActiveCategory('')}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                activeCategory === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {categories.filter(c => c.status === 'active').map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  activeCategory === cat.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Product Grid */}
          <div className="flex-1 overflow-y-auto p-3">
            {filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Package className="mb-2 h-10 w-10 text-gray-300" />
                <p>No products found</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {filteredProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    disabled={product.stockOnHand <= 0}
                    className={`rounded-lg border p-3 text-left transition-all hover:border-blue-400 hover:shadow-md ${
                      product.stockOnHand <= 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="font-mono text-[10px] text-gray-400">{product.sku}</span>
                      {product.stockOnHand <= product.minStockLevel && product.stockOnHand > 0 && (
                        <AlertTriangle className="h-3 w-3 text-yellow-500" />
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-900 line-clamp-2">{product.name}</p>
                    <div className="mt-1 flex items-center justify-between">
                      <p className="text-sm font-bold text-green-700">
                        ₱{(product.retailPrice ?? 0).toFixed(2)}
                      </p>
                      <span className="text-[10px] text-gray-400">
                        Stock: {product.stockOnHand}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Cart Panel */}
        <div className="flex w-96 flex-col bg-white">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-gray-700" />
              <h2 className="font-semibold text-gray-900">
                Cart ({cart.length})
              </h2>
            </div>
            {cart.length > 0 && (
              <button
                onClick={clearCart}
                className="text-xs text-red-600 hover:text-red-800"
              >
                Clear
              </button>
            )}
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <ShoppingCart className="mb-2 h-10 w-10" />
                <p className="text-sm">Cart is empty</p>
                <p className="text-xs">Scan or click products to add</p>
              </div>
            ) : (
              <div className="divide-y">
                {cart.map((item) => (
                  <div key={item.productId} className="p-3 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          ₱{item.unitPrice.toFixed(2)} × {item.quantity}
                        </p>
                        {item.discountPercent > 0 && (
                          <Badge color="red" size="sm">{item.discountPercent}% off</Badge>
                        )}
                      </div>
                      <p className="text-sm font-bold text-gray-900 ml-2">
                        ₱{item.lineTotal.toFixed(2)}
                      </p>
                    </div>

                    <div className="mt-2 flex items-center gap-2">
                      {/* Qty Controls */}
                      <div className="flex items-center rounded border">
                        <button
                          onClick={() => updateQty(item.productId, -1)}
                          className="px-2 py-1 text-gray-500 hover:bg-gray-100"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="px-2 text-xs font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQty(item.productId, 1)}
                          className="px-2 py-1 text-gray-500 hover:bg-gray-100"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>

                      {/* Discount Buttons */}
                      <button
                        onClick={() => updateDiscount(item.productId, item.discountPercent === 10 ? 0 : 10)}
                        className={`rounded px-2 py-1 text-xs ${
                          item.discountPercent === 10
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        10%
                      </button>
                      <button
                        onClick={() => updateDiscount(item.productId, item.discountPercent === 20 ? 0 : 20)}
                        className={`rounded px-2 py-1 text-xs ${
                          item.discountPercent === 20
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        20%
                      </button>

                      <button
                        onClick={() => removeFromCart(item.productId)}
                        className="ml-auto rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart Footer: Totals + Checkout */}
          {cart.length > 0 && (
            <div className="border-t bg-gray-50 p-4 space-y-3">
              {/* Sale-level discount */}
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-gray-500" />
                <select
                  value={saleDiscountPercent}
                  onChange={(e) => setSaleDiscountPercent(Number(e.target.value))}
                  className="flex-1 rounded border px-2 py-1 text-xs"
                >
                  <option value={0}>No discount</option>
                  <option value={5}>5% - Loyalty</option>
                  <option value={10}>10% - Promo</option>
                  <option value={20}>20% - Senior/PWD</option>
                </select>
                {saleDiscountPercent > 0 && (
                  <input
                    type="text"
                    placeholder="Label"
                    value={saleDiscountLabel}
                    onChange={(e) => setSaleDiscountLabel(e.target.value)}
                    className="w-28 rounded border px-2 py-1 text-xs"
                  />
                )}
              </div>

              {/* Totals */}
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>₱{totals.subtotal.toFixed(2)}</span>
                </div>
                {totals.discountTotal > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Discount</span>
                    <span>-₱{totals.discountTotal.toFixed(2)}</span>
                  </div>
                )}
                {totals.vatTotal > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>VAT</span>
                    <span>₱{totals.vatTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-1 text-lg font-bold text-gray-900">
                  <span>Total</span>
                  <span>₱{totals.grandTotal.toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={openPayment}
                className="w-full"
                icon={<Banknote className="h-4 w-4" />}
              >
                Pay ₱{totals.grandTotal.toFixed(2)}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPayment && (
        <Modal open={showPayment} onClose={() => setShowPayment(false)} title="Process Payment">
          <div className="space-y-4 p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                ₱{totals.grandTotal.toFixed(2)}
              </p>
              <p className="text-sm text-gray-500">{cart.length} items</p>
            </div>

            {/* Payment Method */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Payment Method</label>
              <div className="grid grid-cols-4 gap-2">
                {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[])
                  .filter((m) => m !== 'split')
                  .map((method) => (
                    <button
                      key={method}
                      onClick={() => {
                        setSelectedPaymentMethod(method)
                        setSplitPayments([])
                      }}
                      className={`rounded-lg border p-2 text-center text-xs transition-colors ${
                        selectedPaymentMethod === method
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-lg">
                        {method === 'cash' ? '💵' : method === 'gcash' ? '📱' : method === 'card_debit' || method === 'card_credit' ? '💳' : '🏦'}
                      </div>
                      {PAYMENT_METHOD_LABELS[method]}
                    </button>
                  ))}
              </div>
            </div>

            {/* Amount Tendered */}
            {selectedPaymentMethod !== 'split' && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Amount Tendered</label>
                  <input
                    type="number"
                    value={amountTendered}
                    onChange={(e) => setAmountTendered(e.target.value)}
                    className="w-full rounded-lg border px-3 py-2 text-lg font-bold"
                    autoFocus
                  />
                </div>
                <div className="flex flex-wrap gap-1">
                  {QUICK_AMOUNTS.map((amt) => (
                    <button
                      key={amt}
                      onClick={() => {
                        const current = parseFloat(amountTendered) || 0
                        setAmountTendered((current + amt).toFixed(2))
                      }}
                      className="rounded border px-2 py-1 text-xs hover:bg-gray-100"
                    >
                      +₱{amt}
                    </button>
                  ))}
                  <button
                    onClick={() => setAmountTendered(totals.grandTotal.toFixed(2))}
                    className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-xs text-blue-700"
                  >
                    Exact
                  </button>
                </div>

                {/* Change */}
                {parseFloat(amountTendered) >= totals.grandTotal && (
                  <div className="rounded-lg bg-green-50 p-3 text-center">
                    <p className="text-sm text-green-600">Change Due</p>
                    <p className="text-xl font-bold text-green-700">
                      ₱{POSSaleService.computeChange(parseFloat(amountTendered), totals.grandTotal).toFixed(2)}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Split Payment */}
            {selectedPaymentMethod === 'split' && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <select
                    className="rounded border px-2 py-1 text-xs"
                    value={selectedPaymentMethod}
                    onChange={() => {}}
                  >
                    {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[])
                      .filter((m) => m !== 'split')
                      .map((m) => (
                        <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>
                      ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Amount"
                    value={splitAmount}
                    onChange={(e) => setSplitAmount(e.target.value)}
                    className="flex-1 rounded border px-2 py-1 text-xs"
                  />
                  <Button size="sm" onClick={handleSplitAdd}>Add</Button>
                </div>
                {splitPayments.length > 0 && (
                  <div className="space-y-1">
                    {splitPayments.map((sp, i) => (
                      <div key={i} className="flex justify-between rounded bg-gray-50 px-2 py-1 text-xs">
                        <span>{PAYMENT_METHOD_LABELS[sp.method]}</span>
                        <span>₱{sp.amount.toFixed(2)}</span>
                        <button
                          onClick={() => setSplitPayments(splitPayments.filter((_, j) => j !== i))}
                          className="text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-between border-t pt-1 text-xs font-bold">
                      <span>Total Split</span>
                      <span>₱{splitPayments.reduce((s, p) => s + p.amount, 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowPayment(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={processSale} className="flex-1" icon={<Receipt className="h-4 w-4" />}>
                Complete Sale
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Completed Sale Notification */}
      {completedSale && (
        <Modal open={!!completedSale} onClose={() => { setCompletedSale(null); clearCart() }} title="Sale Complete ✅">
          <div className="space-y-4 p-4 text-center">
            <div className="rounded-full bg-green-100 p-4 mx-auto w-fit">
              <Receipt className="h-10 w-10 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{completedSale.invoiceNumber}</p>
              <p className="text-2xl font-bold text-gray-900">₱{completedSale.grandTotal.toFixed(2)}</p>
              <p className="text-sm text-gray-500">
                {completedSale.paymentMethod && PAYMENT_METHOD_LABELS[completedSale.paymentMethod]}
                {completedSale.changeDue > 0 && ` · Change: ₱${completedSale.changeDue.toFixed(2)}`}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setCompletedSale(null); clearCart() }}
                className="flex-1"
              >
                New Sale
              </Button>
              <Button
                variant="outline"
                onClick={voidSale}
                className="flex-1 text-red-600"
              >
                Void Sale
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────

function createCartItem(product: POSProduct, qty: number, discountPercent: number): CartItem {
  const computed = POSSaleService.computeLineTotals({
    quantity: qty,
    unitPrice: product.retailPrice,
    discountPercent,
    vatRate: product.vatRate,
    isVatInclusive: product.isVatInclusive,
  })
  return {
    productId: product.id,
    sku: product.sku,
    barcode: product.barcode,
    name: product.name,
    unit: product.unit,
    quantity: qty,
    unitPrice: product.retailPrice,
    discountPercent,
    discountAmount: computed.discountAmount,
    lineTotal: computed.lineTotal,
    vatAmount: computed.vatAmount,
    vatRate: product.vatRate,
    isVatInclusive: product.isVatInclusive,
    costPrice: product.costPrice,
  }
}

function updateCartItem(item: CartItem, qty: number, discountPercent: number): CartItem {
  const computed = POSSaleService.computeLineTotals({
    quantity: qty,
    unitPrice: item.unitPrice,
    discountPercent,
    vatRate: item.vatRate,
    isVatInclusive: item.isVatInclusive,
  })
  return {
    ...item,
    quantity: qty,
    discountPercent,
    discountAmount: computed.discountAmount,
    lineTotal: computed.lineTotal,
    vatAmount: computed.vatAmount,
  }
}
